import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./MainContent.css";
import Announcements from "./Widgets/Announcements";
import TasksWidget from "./Widgets/TasksWidget";
import LeaderboardWidget from "./Widgets/LeaderboardWidget";
import { Plus, ShieldCheck } from "lucide-react";
import { profileApi, tracksApi } from "../../api/client";
import { getApiErrorMessage } from "../../api/http";
import { clearActiveTrackId, getActiveTrackId, setActiveTrackId } from "../../utils/session";
import { useAuth } from "../../context/AuthContext";
import { isTrackOwnedByUser } from "../../utils/track";

const MainContent = () => {
  const { user } = useAuth();
  const role = user?.role || "student";
  const userId = user?.id || user?._id;

  const [tracks, setTracks] = useState([]);
  const [activeTrack, setActiveTrack] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const loadBase = async () => {
      setLoading(true);
      setError("");
      setNotice("");
      try {
        const { data } = await tracksApi.list();
        const list = data?.tracks || [];

        let accessible = list;
        if (role === "student") {
          const profileRes = await profileApi.me();
          const approvedIds = new Set((profileRes.data?.profile?.tracks || []).map((t) => `${t?._id || t}`));
          accessible = list.filter((track) => approvedIds.has(`${track._id}`));
        } else if (role === "instructor") {
          accessible = list.filter((track) => isTrackOwnedByUser(track, userId));
        }

        setTracks(accessible);

        if (!accessible.length) {
          setActiveTrack(null);
          setTasks([]);
          setAnnouncements([]);
          setLeaderboard([]);
          clearActiveTrackId();
          setNotice(
            role === "student"
              ? "You are not approved in any track yet."
              : role === "instructor"
                ? "No tracks are assigned to your instructor account yet."
                : "No tracks available."
          );
          return;
        }

        const savedTrackId = getActiveTrackId();
        const selected = accessible.find((x) => x._id === savedTrackId) || accessible[0];
        setActiveTrack(selected);
        setActiveTrackId(selected._id);
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to load dashboard tracks"));
      } finally {
        setLoading(false);
      }
    };

    loadBase();
  }, [role, userId]);

  useEffect(() => {
    const loadTrackData = async () => {
      if (!activeTrack?._id) return;
      setLoading(true);
      setError("");
      try {
        const [tasksRes, annRes, lbRes] = await Promise.all([
          tracksApi.tasks(activeTrack._id),
          tracksApi.announcements(activeTrack._id),
          tracksApi.trackLeaderboard(activeTrack._id),
        ]);
        setTasks(tasksRes.data?.tasks || []);
        setAnnouncements(annRes.data?.announcements || []);
        setLeaderboard(lbRes.data?.leaderboard || []);
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to load dashboard data"));
      } finally {
        setLoading(false);
      }
    };

    loadTrackData();
  }, [activeTrack?._id]);

  const pendingTasksCount = useMemo(() => {
    const now = Date.now();
    return tasks.filter((task) => new Date(task.deadline).getTime() >= now).length;
  }, [tasks]);

  const handleTrackChange = (event) => {
    const next = tracks.find((track) => track._id === event.target.value);
    if (!next) return;
    setActiveTrack(next);
    setActiveTrackId(next._id);
  };

  const actionButton = useMemo(() => {
    if (role === "instructor") {
      return (
        <Link to="/create-task" className="btn submit-task-btn btn-primary text-decoration-none">
          <Plus size={18} className="me-2" />
          Create Task
        </Link>
      );
    }
    if (role === "super_admin") {
      return (
        <Link to="/super-admin" className="btn submit-task-btn btn-primary text-decoration-none">
          <ShieldCheck size={18} className="me-2" />
          Control Panel
        </Link>
      );
    }
    return (
      <Link to="/assignment" className={`btn submit-task-btn btn-primary text-decoration-none ${!activeTrack?._id ? "disabled" : ""}`}>
        <Plus size={18} className="me-2" />
        Submit Task
      </Link>
    );
  }, [activeTrack?._id, role]);

  return (
    <div className="main-content">
      <header className="main-header">
        <div className="header-text">
          <h1 className="page-title">{activeTrack?.name || "Track Dashboard"}</h1>
          <p className="page-subtitle">
            Welcome back, {user?.fullName || "User"}. You have{" "}
            <span className="highlight">{pendingTasksCount} pending tasks</span> today.
          </p>
        </div>
        <div className="dashboard-track-actions d-flex gap-2 align-items-center">
          <select className="form-select form-select-sm dashboard-track-select" value={activeTrack?._id || ""} onChange={handleTrackChange} disabled={!tracks.length}>
            {tracks.map((track) => (
              <option key={track._id} value={track._id}>
                {track.name}
              </option>
            ))}
          </select>
          {actionButton}
        </div>
      </header>

      {notice ? <div className="alert alert-warning py-2">{notice}</div> : null}
      {loading ? <div className="alert alert-info py-2">Loading dashboard data...</div> : null}
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      <div className="widgets-grid">
        <div className="widget-column notifications-col">
          <Announcements items={announcements} />
        </div>

        <div className="widget-column tasks-col">
          <TasksWidget tasks={tasks} />
        </div>

        <div className="widget-column leaderboard-col">
          <LeaderboardWidget leaderboard={leaderboard} currentUserId={user?.id || user?._id} />
        </div>
      </div>
    </div>
  );
};

export default MainContent;
